import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {useService} from '../../../../infrastructure/service/service';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select, SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {UserInvitationDialogProps} from '../../../../entity/props/users/user-invitation-dialog-props';
import LoadingComponent from '../widget/LoadingComponent';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import {getUserRoles} from '../../../../entity/enums/user-role';
import {Organization} from '../../../../entity/model/organization';
import {UserInvitationDto} from '../../../../entity/dto/user-invitation-dto';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';

import '../../../../assets/css/style.css';
import '../../../../assets/css/user/user-dialog.css';

const UserInvitationDialog = (props: UserInvitationDialogProps): JSX.Element => {
  const {organizationService, userService} = useService();

  const [organization, setOrganization] = useState<Organization>();
  const [loading, setLoading] = useState<boolean>(false);
  const [roleOptions, setRoleOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      setLoading(true);

      const user = await userService.getCurrentUser();
      const organization = await organizationService.getOrganizationById(user?.organizationID ?? '');
      setOrganization(organization);

      setRoleOptions(getUserRoles(organization?.isPublic ?? false).map((role: string): DropdownOption => ({key: role, text: role, value: role})));

      setLoading(false);
    }

    init();
  }, []);

  const handleValidate = (values: UserInvitationDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.email)
      errors.email = 'Email is required.';

    if (!values.roles)
      errors.roles = 'Role is required.';

    if (!values.customExpirationDays)
      errors.customExpirationDays = 'Expiration Days is required.';

    return errors;
  }

  const handleSubmit = async (values: UserInvitationDto, {setSubmitting}: FormikHelpers<UserInvitationDto>): Promise<void> => {
    setSubmitting(true);

    const invitationDto: UserInvitationDto = {
      email: values.email,
      roles: [values.roles as string],
      customExpirationDays: values.customExpirationDays
    };

    await userService.sendInvitation(organization?.id ?? '', invitationDto);

    setSubmitting(false);
    props.setOpen(false);
  }

  return (
    <>
      {
        loading
          ? <LoadingComponent/>
          : <Dialog open={props.open}>
              <DialogTitle className={'zitles-dialog-title'}>
                <span>Invite User</span>
                <IconButton id={'btn-user-dialog-close'} onClick={(): void => props.setOpen(false)}>
                  <CloseIcon className={'dialog-icon'} />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers className={'user-dialog-content'}>
                <Paper elevation={0}>
                  <Formik
                    initialValues={{
                      email: '',
                      roles: '',
                      customExpirationDays: 30,
                      organizationID: organization?.id ?? ''
                    } as UserInvitationDto}
                    validate={handleValidate}
                    onSubmit={handleSubmit}>
                    {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting}: FormikProps<UserInvitationDto>): JSX.Element => (
                      <Form onSubmit={handleSubmit}>
                        <Card elevation={0} className={'template-dialog-card info'}>
                          <CardContent className={'user-info-card-content'}>
                            <Box className={`template-item ${touched.email && Boolean(errors.email) ? 'form' : 'template'}`}>
                              <Typography className={`template-data-label user ${touched.email && Boolean(errors.email) ? 'form' : 'view'}`}>Email</Typography>
                              <TextField
                                id={'input-user-email'}
                                required
                                fullWidth
                                label='Email'
                                name='email'
                                className={'template-input user'}
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.email && Boolean(errors.email)}
                                helperText={touched.email && errors.email}/>
                            </Box>
                            <Box className={`template-item ${touched.roles && Boolean(errors.roles) ? 'form' : 'template'}`}>
                              <Typography className={`template-data-label user ${touched.roles && Boolean(errors.roles) ? 'form' : 'view'}`}>Role</Typography>
                              <FormControl required fullWidth error={touched.roles && Boolean(errors.roles)} className={'template-input user'}>
                                <InputLabel>Role</InputLabel>
                                <Select
                                  id={'input-user-role'}
                                  name='roles'
                                  value={Array.isArray(values.roles) ? '' : (values.roles || '')}
                                  label='Role'
                                  onBlur={handleBlur}
                                  onChange={(event: SelectChangeEvent<string>) => handleChange({target: {name: event.target.name, value: event.target.value}})}>
                                  {
                                    roleOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                                  }
                                </Select>
                                <FormHelperText>{touched.roles && typeof errors.roles === 'string' ? errors.roles : ''}</FormHelperText>
                              </FormControl>
                            </Box>
                            <Box className={`template-item ${touched.customExpirationDays && Boolean(errors.customExpirationDays) ? 'form' : 'template'}`}>
                              <Typography className={`template-data-label user ${touched.customExpirationDays && Boolean(errors.customExpirationDays) ? 'form' : 'view'}`}>Expiration Days</Typography>
                              <TextField
                                id={'input-user-expiration-days'}
                                required
                                fullWidth
                                type='number'
                                label='Expiration Days'
                                name='customExpirationDays'
                                className={'template-input user'}
                                value={values.customExpirationDays}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.customExpirationDays && Boolean(errors.customExpirationDays)}
                                helperText={touched.customExpirationDays && errors.customExpirationDays}
                                InputLabelProps={{
                                  shrink: true
                                }}/>
                            </Box>
                            <Box className={'template-item template'}>
                              <Typography className={'template-data-label user view'}>Organization</Typography>
                              <TextField
                                id={'input-user-organization'}
                                required
                                fullWidth
                                disabled
                                className={'template-input user'}
                                value={organization?.name ?? ''}/>
                            </Box>
                          </CardContent>
                        </Card>
                        <Divider sx={{ my: 2, mx: -3 }} />
                        <Box className={'action-btn-container'}>
                          <Button
                            id={'btn-user-cancel'}
                            disableTouchRipple
                            onClick={(): void => props.setOpen(false)}
                            className={'zitles-button cancel'}>
                            Cancel
                          </Button>
                          <Button
                            id={'btn-user-submit'}
                            type='submit'
                            disabled={isSubmitting}
                            className={`zitles-button submit ${isSubmitting ? 'disable' : ''}`}
                            startIcon={isSubmitting ? <CircularProgress size={14} color='inherit'/> : null}>
                            {
                              isSubmitting ? 'Sending Invitation...' : 'Send Invitation'
                            }
                          </Button>
                        </Box>
                      </Form>
                    )}
                  </Formik>
                </Paper>
              </DialogContent>
            </Dialog>
      }
    </>
  );
}

export default observer(UserInvitationDialog);